import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import {
  chatbotScannedUrlResponse,
  ChatbotWidgetConfiguration,
  GetBotResponse,
  PostRegistryBotResponse,
  PostRequestBot,
  PutUpdateBotRequest,
  ResponseGetBotInterface,
} from '../../models/bot.interface';
import { ChatHistoryGroup } from '../../models/history.interface';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { LoggerService } from './logger.service';
import { Faq, FaqRequest, FaqListResponse, ReorderFaqsRequest } from '../../models/faq.interface';

@Injectable({
  providedIn: 'root',
})
export class BotService {
  private apiAddress: string = environment.apiAddress;
  private haveBotSubject = new BehaviorSubject<boolean>(false);
  readonly haveBots$ = this.haveBotSubject.asObservable();

  private botCache = new Map<string, BehaviorSubject<ResponseGetBotInterface | null>>();

  constructor(private http: HttpClient, private logger: LoggerService) {}
  getBot$(id: string): Observable<ResponseGetBotInterface | null> {
    if (!this.botCache.has(id)) {
      this.botCache.set(id, new BehaviorSubject<ResponseGetBotInterface | null>(null));
      this.loadBot(id);
    }
    return this.botCache.get(id)!.asObservable();
  }

  private loadBot(id: string): void {
    this.http.get<ResponseGetBotInterface>(`${this.apiAddress}/bot/${id}`).subscribe({
      next: (bot) => this.botCache.get(id)?.next(bot),
      error: (err) => this.logger.error('Error loading bot', err),
    });
  }

  getBots() {
    return this.http.get<GetBotResponse[]>(`${this.apiAddress}/bot`);
  }

  getBot(id: string) {
    return this.http.get<ResponseGetBotInterface>(`${this.apiAddress}/bot/${id}`);
  }

  generateSampleQuestions(id: string): Observable<string[]> {
    return this.http
      .post<{ code: number; message: string; data: { questions: string[] } }>(
        `${this.apiAddress}/bot/${id}/sample-questions/generate`,
        {}
      )
      .pipe(map((res) => res?.data?.questions ?? []));
  }

  deleteBot(id: string) {
    return this.http.delete(`${this.apiAddress}/bot/${id}`);
  }

  postBot(data: PostRequestBot) {
    return this.http.post<PostRegistryBotResponse>(`${this.apiAddress}/bot`, data);
  }

  uploadSource(data: string, id: string) {
    return this.http.post<any>(`${this.apiAddress}/bot/${id}/source_upload/url`, data);
  }

  scanUrl(data: any, id: string) {
    return this.http.post<any>(`${this.apiAddress}/bot/${id}/source_upload/url_scan`, data);
  }

  getScannedUrls(jobId: string, botId: string) {
    return this.http.get<chatbotScannedUrlResponse>(
      `${this.apiAddress}/bot/${botId}/source_upload/url_scan/${jobId}/results`
    );
  }

  deleteScannedUrl(jobId: string, url_id: string) {
    return this.http.delete(`${this.apiAddress}/bot-job/${jobId}/scanned_url/${url_id}`);
  }

  deleteJobId(jobId: string) {
    return this.http.delete(`${this.apiAddress}/bot-job/${jobId}/delete`);
  }

  uploadUrlsByJob(botId: string, jobId: string) {
    return this.http.post<any>(`${this.apiAddress}/bot/${botId}/upload/job/${jobId}`, null);
  }

  uploadSourceFile(file: File, id: string) {
    const formData: FormData = new FormData();
    formData.append('file', file);
    return this.http.post<any>(`${this.apiAddress}/bot/${id}/source_upload`, formData);
  }

  sendMessage(data: any, id: string, origin?: string, channel?: string) {
    let httpHeaders: HttpHeaders | undefined = undefined;
    if (channel || origin) {
      httpHeaders = new HttpHeaders();
    }

    if (channel && httpHeaders) {
      httpHeaders = httpHeaders.append('GLOBOTCHANNEL', channel);
    }

    if (origin && httpHeaders) {
      httpHeaders = httpHeaders.append('GLOBOTORIGIN', origin);
    }

    return this.http.post<any>(`${this.apiAddress}/bot/${id}/prompt`, data, {
      headers: httpHeaders,
    });
  }

  sendExperimentalMessage(
    data: { prompt: string; origin?: string; sender_id?: string; sender_name?: string },
    token: string,
    correlationId: string,
    variant: 'A' | 'B' | 'C'
  ): Observable<{ response: string; chatbot_history_id?: string }> {
    const variantPathMap: Record<'A' | 'B' | 'C', string> = {
      A: 'prompt-supervisor',
      B: 'prompt-handoff',
      C: 'prompt-bidirectional',
    };
    const path = variantPathMap[variant];
    const headers = new HttpHeaders({ 'correlation-id': correlationId });
    return this.http.post<{ response: string; chatbot_history_id?: string }>(
      `${this.apiAddress}/bot/${token}/${path}`,
      data,
      { headers }
    );
  }

  updateBot(data: PutUpdateBotRequest, id: string) {
    return this.http.put<any>(`${this.apiAddress}/bot/${id}`, data);
  }

  getRecords(id: string, rangeDate?: [string, string], options?: { lastHours?: number }) {
    const params: Record<string, string | number> = {};
    if (options?.lastHours != null) {
      params['last_hours'] = options.lastHours;
    } else if (rangeDate) {
      params['init'] = rangeDate[0];
      params['term'] = rangeDate[1];
    }
    return this.http.get<GetBotResponse[]>(`${this.apiAddress}/bot/${id}/history`, { params });
  }
  /**
   * Detalle de una conversación, agrupado por fecha.
   *
   * GLOB-1888: se eliminaron los parámetros `chatsFrom`/`to`. El backend nunca los
   * leyó, así que "cargar más" repetía la misma lista completa y la duplicaba en la
   * vista. El endpoint devuelve la conversación entera (promedio 2,9 mensajes).
   *
   * `group=true` agrupa el hilo por Client en canales Meta; `false` acota al
   * correlation_id pedido. La respuesta siempre viene agrupada por fecha.
   */
  getChatHistory(botId: string, senderId: string, group: boolean = true) {
    return this.http.get<ChatHistoryGroup[]>(
      `${this.apiAddress}/bot/${botId}/history/chat/${senderId}?group=${group}`
    );
  }

  haveBots(): boolean {
    return this.haveBotSubject.value;
  }

  setHaveBots(value: boolean): void {
    this.haveBotSubject.next(value);
  }

  getRecordsSummary(id: string) {
    return this.http.get<GetBotResponse[]>(`${this.apiAddress}/bot/${id}/history/summary`);
  }

  getWelcomeMessage(id: string) {
    return this.http.get<any>(`${this.apiAddress}/bot/${id}/welcome-message`);
  }

  updateWelcomeMessage(id: string, welcomeMessage: any) {
    return this.http.put(`${this.apiAddress}/bot/${id}/welcome-message`, welcomeMessage);
  }

  saveWidgetConfig(id: string, chatbotWidgetConfiguration: ChatbotWidgetConfiguration) {
    return this.http.put(`${this.apiAddress}/bot/${id}/widget_config`, chatbotWidgetConfiguration);
  }
  getWidget(id: string) {
    return this.http.get<any>(`${this.apiAddress}/bot/${id}/widget_config`);
  }

  // FAQ methods
  getFaqs(botId: string) {
    return this.http
      .get<{ code: number; message: string; data: FaqListResponse }>(
        `${this.apiAddress}/bot/${botId}/faqs`
      )
      .pipe(map((response) => response.data));
  }

  createFaq(botId: string, faq: FaqRequest) {
    return this.http
      .post<{ code: number; message: string; data: Faq }>(
        `${this.apiAddress}/bot/${botId}/faqs`,
        faq
      )
      .pipe(map((response) => response.data));
  }

  updateFaq(botId: string, faqId: string, faq: Partial<FaqRequest>) {
    return this.http
      .put<{ code: number; message: string; data: Faq }>(
        `${this.apiAddress}/bot/${botId}/faqs/${faqId}`,
        faq
      )
      .pipe(map((response) => response.data));
  }

  deleteFaq(botId: string, faqId: string) {
    return this.http.delete(`${this.apiAddress}/bot/${botId}/faqs/${faqId}`);
  }

  reorderFaqs(botId: string, reorderData: ReorderFaqsRequest) {
    return this.http
      .put<{ code: number; message: string; data: FaqListResponse }>(
        `${this.apiAddress}/bot/${botId}/faqs/reorder`,
        reorderData
      )
      .pipe(map((response) => response.data));
  }
}
